import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Nuværende bruger data
  currentUser: null,
  // Alle brugere i systemet
  allUsers: [],
  // Valgt bruger (til admin funktioner)
  selectedUser: null,
  // Loading states
  loading: false,
  usersLoading: false,
  // Error states
  error: null,
  usersError: null,
};

const mateSlice = createSlice({
  name: 'mate',
  initialState,
  reducers: {
    // Load mate state from localStorage on app start
    loadMateFromStorage: (state) => {
      try {
        const userRaw = localStorage.getItem('user');
        if (!userRaw) return;
        const user = JSON.parse(userRaw);
        if (user && typeof user === 'object') {
          state.currentUser = user;
        }
      } catch (e) {
        // Hvis localStorage data er korrupt, så ignorér det stille og roligt
      }
    },
    // Actions til nuværende bruger
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
      state.loading = false;
      state.error = null;
    },
    updateCurrentUser: (state, action) => {
      if (state.currentUser) {
        state.currentUser = { ...state.currentUser, ...action.payload };
      }
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
      state.error = null;
    },
    
    // Actions til alle brugere
    setAllUsers: (state, action) => {
      state.allUsers = action.payload;
      state.usersLoading = false;
      state.usersError = null;
    },
    addUser: (state, action) => {
      state.allUsers.push(action.payload);
    },
    updateUser: (state, action) => {
      const { userId, updates } = action.payload;
      const userIndex = state.allUsers.findIndex(user => user.id === userId);
      if (userIndex !== -1) {
        state.allUsers[userIndex] = { ...state.allUsers[userIndex], ...updates };
      }
      // Hvis det er nuværende bruger, opdater også currentUser
      if (state.currentUser && state.currentUser.id === userId) {
        state.currentUser = { ...state.currentUser, ...updates };
      }
    },
    removeUser: (state, action) => {
      state.allUsers = state.allUsers.filter(user => user.id !== action.payload);
    },
    
    // Actions til valgt bruger
    setSelectedUser: (state, action) => {
      state.selectedUser = action.payload;
    },
    clearSelectedUser: (state) => {
      state.selectedUser = null;
    },
    
    // Loading states
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setUsersLoading: (state, action) => {
      state.usersLoading = action.payload;
    },
    
    // Error states
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setUsersError: (state, action) => {
      state.usersError = action.payload;
      state.usersLoading = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearUsersError: (state) => {
      state.usersError = null;
    },
    clearAllErrors: (state) => {
      state.error = null;
      state.usersError = null;
    },
    
    // Reset hele state
    resetMateState: (state) => {
      return initialState;
    },
  },
});

export const {
  // Current user actions
  loadMateFromStorage,
  setCurrentUser,
  updateCurrentUser,
  clearCurrentUser,
  
  // All users actions
  setAllUsers,
  addUser,
  updateUser,
  removeUser,
  
  // Selected user actions
  setSelectedUser,
  clearSelectedUser,
  
  // Loading actions
  setLoading,
  setUsersLoading,
  
  // Error actions
  setError,
  setUsersError,
  clearError,
  clearUsersError,
  clearAllErrors,
  
  // Reset action
  resetMateState,
} = mateSlice.actions;

// Selectors til nem adgang til mate data
export const selectCurrentUser = (state) => state.mate.currentUser;
export const selectAllUsers = (state) => state.mate.allUsers;
export const selectSelectedUser = (state) => state.mate.selectedUser;
export const selectMateLoading = (state) => state.mate.loading;
export const selectUsersLoading = (state) => state.mate.usersLoading;
export const selectMateError = (state) => state.mate.error;
export const selectUsersError = (state) => state.mate.usersError;

// Computed selectors
export const selectIsCurrentUserAdmin = (state) => state.mate.currentUser?.role === 'admin';
export const selectIsCurrentUserUser = (state) => state.mate.currentUser?.role === 'user';
export const selectCurrentUserRole = (state) => state.mate.currentUser?.role;
export const selectCurrentUserName = (state) => state.mate.currentUser?.name;
export const selectCurrentUserEmail = (state) => state.mate.currentUser?.email;
export const selectCurrentUserId = (state) => state.mate.currentUser?.id;

// Selector til at finde en specifik bruger
export const selectUserById = (userId) => (state) => 
  state.mate.allUsers.find(user => user.id === userId);

// Selector til at finde brugere efter role
export const selectUsersByRole = (role) => (state) => 
  state.mate.allUsers.filter(user => user.role === role);

// Selector til at tjekke om en bruger er admin
export const selectIsUserAdmin = (userId) => (state) => {
  const user = state.mate.allUsers.find(user => user.id === userId);
  return user?.role === 'admin';
};

// Selector til at tjekke om en bruger er user
export const selectIsUserUser = (userId) => (state) => {
  const user = state.mate.allUsers.find(user => user.id === userId);
  return user?.role === 'user';
};

// Selector til at få admin brugere
export const selectAdminUsers = (state) => 
  state.mate.allUsers.filter(user => user.role === 'admin');

// Selector til at få almindelige brugere
export const selectRegularUsers = (state) => 
  state.mate.allUsers.filter(user => user.role === 'user');

export default mateSlice.reducer;
